import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { useBootstrapSelector } from '../../../redux/selectorHooks';
import { assertNotNil } from '../../../utils/types';
import { useCheckFeatureFunction } from '../../../utils/hooks';
import PermissionElementWrapper from '../../../pages/PermissionControls/PermissionElementWrapper';
import { headerMainNavigationLinks } from '../utils';
import { appRoutes } from '../../../path';
import { ReactComponent as KubitBrandmark } from '../../../assets/images/kubit-brandmark.svg';
import { theme } from '../../../styles/theme';

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const MainNavItem = styled(NavLink)<{ $isDisabled: boolean }>`
  flex: 0 0 auto;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: rgba(255, 255, 255, 0.8);
  &:hover {
    color: ${theme.colors.white};
    background-color: rgba(255, 255, 255, 25%);
  }
  &.active {
    color: ${theme.colors.white};
    background-color: rgba(255, 255, 255, 10%);
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    pointer-events: none;
    opacity: 0.3;
  `};
`;

const LogoContainer = styled(Link)`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LinksContainer = styled.nav`
  display: flex;
  align-items: center;
  column-gap: 16px;
`;

const HeaderMainNavigation = () => {
  const location = useLocation();

  const { checkIsPermitted } = usePermission();
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);
  const checkFeatureFunc = useCheckFeatureFunction();

  return (
    <Container>
      <LogoContainer to={appRoutes.home}>
        <KubitBrandmark width={32} height={32} fill={theme.colors.white} />
      </LogoContainer>

      <LinksContainer>
        {headerMainNavigationLinks
          .filter((item) => {
            if (item?.requiredFeature) {
              return checkFeatureFunc(item.requiredFeature);
            }
            return true;
          })
          .map((item) => {
            const isPermittedForCurrentKey = item?.permissionKeys
              ? checkIsPermitted({
                  requiredPermissionKeys: item.permissionKeys,
                  validateKind: 'OR',
                })
              : true;

            let url = item.url;

            // prevent navigation to workspace or dashboard when clicking on them
            // while they are already active
            if (
              location.pathname.startsWith(item.url) &&
              (item.url === appRoutes.workspaces.routePrefix ||
                item.url === appRoutes.dashboard.routePrefix)
            ) {
              url = '#';
            }
            return (
              <PermissionElementWrapper
                key={item.label}
                isPermitted={isPermittedForCurrentKey}
              >
                <MainNavItem to={url} $isDisabled={!isPermittedForCurrentKey}>
                  {item.label}
                </MainNavItem>
              </PermissionElementWrapper>
            );
          })}
      </LinksContainer>
    </Container>
  );
};

export default HeaderMainNavigation;
